/**
 * Reimbursement Internal Notes — list page (Angular "frequently-used-notes-config").
 * Client-side list: load-all, submit-search, client pagination, HTML5 drag-reorder.
 * Add / Edit via modal; delete via confirm dialog.
 */

import { useState } from 'react'
import { GripVertical, Pencil, Trash2 } from 'lucide-react'
import { showSuccess, showApiError } from '@/lib/utils/notifications'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { ListingDataTableCard, LISTING_DATA_TABLE_CLASS } from '@/components/ui/listingDataTable'
import { ListPaginationBar } from '@/components/ui/ListPaginationBar'
import { PageShell } from '@/components/layout/PageShell'
import { ListingTableLoadingRow } from '@/components/ui/ModuleScreenLoading'
import { primaryEntityActionButtonClass, secondaryEntityActionButtonClass } from '@/components/ui/entityActionButtonClasses'
import parityStyles from '@/features/invitee/styles/invitesParity.module.css'
import { cn } from '@/lib/utils/helpers'
import { ReimbursementNoteEditModal } from '../components/ReimbursementNoteEditModal'
import {
  useReorderableClientList,
} from '../hooks/useReorderableClientList'
import { BUSINESS_CONFIG_PER_PAGE_OPTIONS } from '../constants'
import {
  deleteReimbursementNote,
  getReimbursementNotes,
  reorderReimbursementNotes,
} from '../services/reimbursementNotesService'
import type { ReimbursementNote } from '../types'

const getReimbursementNoteId = (note: ReimbursementNote): number => note.notes_check_list_id
const reimbursementNoteMatchesSearch = (note: ReimbursementNote, query: string): boolean =>
  (note.note_text || '').toLowerCase().includes(query)

export function ReimbursementNotesListPage() {
  const list = useReorderableClientList<ReimbursementNote>({
    fetchAll: getReimbursementNotes,
    getId: getReimbursementNoteId,
    matchesSearch: reimbursementNoteMatchesSearch,
    persistOrder: reorderReimbursementNotes,
    reorderSuccessMessage: 'Order updated.',
    searchGuardMessage: 'Clear search before reordering notes.',
  })

  const [editOpen, setEditOpen] = useState(false)
  const [editTarget, setEditTarget] = useState<ReimbursementNote | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<ReimbursementNote | null>(null)

  const openAdd = () => {
    setEditTarget(null)
    setEditOpen(true)
  }
  const openEdit = (note: ReimbursementNote) => {
    setEditTarget(note)
    setEditOpen(true)
  }

  const confirmDelete = async () => {
    if (!deleteTarget) return
    try {
      await deleteReimbursementNote(deleteTarget.notes_check_list_id)
      showSuccess('Message removed.')
      setDeleteTarget(null)
      void list.reload()
    } catch (error) {
      showApiError(error)
    }
  }

  return (
    <PageShell
      title="REIMBURSEMENT INTERNAL NOTES"
      headerAction={
        <button type="button" onClick={openAdd} className={cn(primaryEntityActionButtonClass, 'w-full sm:w-auto')}>
          Add
        </button>
      }
    >
      <form
        className="mb-4 flex flex-wrap items-center gap-2"
        onSubmit={(event) => {
          event.preventDefault()
          list.submitSearch()
        }}
      >
        <input
          type="text"
          value={list.searchInput}
          onChange={(event) => list.setSearchInput(event.target.value)}
          placeholder="Search.."
          className="min-w-0 flex-1 sm:max-w-xs px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <button type="submit" disabled={list.loading} className={cn(primaryEntityActionButtonClass)}>
          Search
        </button>
        <button type="button" onClick={list.resetSearch} className={cn(secondaryEntityActionButtonClass)}>
          Reset
        </button>
      </form>

      <ListingDataTableCard>
        <div className="w-full">
          <table className={cn(parityStyles.propertyListingTable, parityStyles.listingTable, LISTING_DATA_TABLE_CLASS)}>
            <thead className="border-b border-[#c8d7e8] bg-[#dfeaf5] text-slate-900">
              <tr>
                <th className="px-2 py-2 !text-center text-xs sm:px-4 sm:py-3 sm:text-sm w-16">S.No.</th>
                <th className="px-2 py-2 !text-center text-xs sm:px-4 sm:py-3 sm:text-sm w-16">Reorder</th>
                <th className="px-2 py-2 !text-left text-xs sm:px-4 sm:py-3 sm:text-sm">Message</th>
                <th className="px-2 py-2 !text-center text-xs sm:px-4 sm:py-3 sm:text-sm w-28">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#dfe7f0] [&>tr:nth-child(even)]:bg-[#f2f7fd]">
              {list.loading ? (
                <ListingTableLoadingRow colSpan={4} />
              ) : list.pageItems.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-3 py-6 text-center text-sm text-slate-500 sm:px-4 sm:py-8">
                    No Records Found. Please try a different Search or Filter from the drop-downs above.
                  </td>
                </tr>
              ) : (
                list.pageItems.map((note, index) => (
                  <tr
                    key={note.notes_check_list_id}
                    className={cn(
                      parityStyles.listingTableRow,
                      list.dnd.draggingId === note.notes_check_list_id && 'opacity-40',
                    )}
                    onDragOver={list.dnd.onRowDragOver}
                    onDragEnter={list.dnd.onRowDragEnter(index)}
                    onDrop={list.dnd.onRowDrop}
                  >
                    <td className="px-2 py-2 text-center text-sm text-slate-700 sm:px-4 sm:py-3">
                      {list.pageStart + index + 1}
                    </td>
                    <td className="px-2 py-2 text-center sm:px-4 sm:py-3">
                      <button
                        type="button"
                        draggable
                        onDragStart={list.dnd.onGripDragStart(index)}
                        onDragEnd={list.dnd.onGripDragEnd}
                        className="cursor-grab p-1.5 rounded text-slate-500 hover:bg-slate-100 active:cursor-grabbing"
                        aria-label="Drag to reorder"
                        title="Drag to reorder"
                      >
                        <GripVertical className="h-4 w-4" />
                      </button>
                    </td>
                    <td className="whitespace-pre-line px-2 py-2 text-sm text-slate-700 sm:px-4 sm:py-3 sm:text-base">
                      {note.note_text}
                    </td>
                    <td className="px-2 py-2 sm:px-4 sm:py-3">
                      <div className="flex justify-center gap-2">
                        <button
                          type="button"
                          onClick={() => openEdit(note)}
                          className="p-1.5 rounded hover:bg-blue-50 text-blue-600"
                          aria-label="Edit"
                          title="Edit"
                        >
                          <Pencil className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => setDeleteTarget(note)}
                          className="p-1.5 rounded hover:bg-red-50 text-red-600"
                          aria-label="Remove"
                          title="Remove"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </ListingDataTableCard>

      {list.totalRecords > 0 && (
        <ListPaginationBar
          placement="footer"
          testIdPrefix="reimbursement-notes"
          currentPage={list.page}
          lastPage={list.lastPage}
          totalRecords={list.totalRecords}
          perPage={list.perPage}
          perPageOptions={BUSINESS_CONFIG_PER_PAGE_OPTIONS}
          onPageChange={list.setPage}
          onPerPageChange={list.changePerPage}
        />
      )}

      <ReimbursementNoteEditModal
        isOpen={editOpen}
        target={editTarget}
        onClose={() => {
          setEditOpen(false)
          setEditTarget(null)
        }}
        onSaved={() => {
          setEditOpen(false)
          setEditTarget(null)
          void list.reload()
        }}
      />

      <ConfirmDialog
        isOpen={deleteTarget !== null}
        title="Confirm"
        message="Do you want to remove this message? Existing text in Internal Notes is not changed."
        primaryLabel="Yes"
        onPrimary={confirmDelete}
        secondaryLabel="No"
        onSecondary={() => setDeleteTarget(null)}
        variant="warning"
      />
    </PageShell>
  )
}
